import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, FileDown, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

const parseNum = (v) => {
  const n = parseFloat(String(v).replace(",", "."));
  return Number.isNaN(n) ? null : n;
};

export const PricingBlock = ({ lead, onChanged }) => {
  const existing = lead.payload?.pricing;
  const [price, setPrice] = useState(existing?.provider_price ?? "");
  const [margin, setMargin] = useState(existing?.margin_pct ?? "");
  const [diagnosis, setDiagnosis] = useState(existing?.diagnosis || "");
  const [solution, setSolution] = useState(existing?.solution || "");
  const [deadline, setDeadline] = useState(existing?.deadline || "");
  const [warranty, setWarranty] = useState(existing?.warranty || "");
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    const pr = lead.payload?.pricing;
    setPrice(pr?.provider_price ?? "");
    setMargin(pr?.margin_pct ?? "");
    setDiagnosis(pr?.diagnosis || "");
    setSolution(pr?.solution || "");
    setDeadline(pr?.deadline || "");
    setWarranty(pr?.warranty || "");
  }, [lead?.id, lead.payload?.pricing?.updated_at]);

  const p = parseNum(price);
  const m = parseNum(margin);
  const valid = p !== null && p >= 0 && m !== null && m >= 0;
  const finalPrice = valid ? p * (1 + m / 100) : null;
  const approvedAt = lead.payload?.quote_approved_at;

  const save = async () => {
    if (!valid) {
      toast.error("Informe o preço do prestador e a margem de lucro.");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/admin/leads/${lead.id}`, {
        pricing: {
          provider_price: p,
          margin_pct: m,
          diagnosis,
          solution,
          deadline,
          warranty,
        },
      });
      toast.success("Orçamento registrado.");
      onChanged();
    } catch {
      toast.error("Falha ao registrar orçamento.");
    } finally {
      setSaving(false);
    }
  };

  const downloadQuote = async () => {
    if (!lead.payload?.pricing) {
      toast.error("Salve o orçamento antes de baixar o PDF.");
      return;
    }
    setDownloading(true);
    const fetchPdf = () => api.get(`/admin/leads/${lead.id}/quote`, { responseType: "blob" });
    try {
      let res;
      try {
        res = await fetchPdf();
      } catch (e) {
        if (e.response?.status === 401) {
          await api.post("/auth/refresh");
          res = await fetchPdf();
        } else {
          throw e;
        }
      }
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `orcamento-resolv-${lead.id.slice(-6)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Falha ao gerar o orçamento em PDF.");
    } finally {
      setDownloading(false);
    }
  };

  const approve = async () => {
    setApproving(true);
    try {
      const { data } = await api.post("/admin/approval", { lead_id: lead.id });
      const digits = (data.phone || "").replace(/\D/g, "");
      const full = digits.startsWith("55") ? digits : `55${digits}`;
      window.open(`https://wa.me/${full}?text=${encodeURIComponent(data.text)}`, "_blank", "noopener");
      toast.success("Aprovação registrada — confirme o envio no WhatsApp.");
      onChanged();
    } catch (e) {
      toast.error(
        typeof e.response?.data?.detail === "string"
          ? e.response.data.detail
          : "Falha ao aprovar orçamento."
      );
    } finally {
      setApproving(false);
    }
  };

  return (
    <div
      className="mt-3 rounded-xl border border-slate-200 bg-white p-4"
      data-testid="pricing-block"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
          Orçamento do prestador (WhatsApp)
        </p>
        {approvedAt && (
          <p
            className="inline-flex items-center gap-1.5 text-[0.65rem] font-semibold text-emerald-600"
            data-testid="pricing-approved-at"
          >
            <CheckCircle2 className="h-3 w-3" />
            Aprovado em{" "}
            {new Date(approvedAt).toLocaleString("pt-BR", {
              dateStyle: "short",
              timeStyle: "short",
            })}
          </p>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-4">
        <label className="space-y-1">
          <span className="block text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
            Preço do prestador
          </span>
          <div className="flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20">
            <span className="mr-1.5 text-xs text-slate-400">R$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              data-testid="pricing-provider-price"
              placeholder="0,00"
              className="w-24 bg-transparent text-sm outline-none"
            />
          </div>
        </label>
        <label className="space-y-1">
          <span className="block text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
            Margem de lucro
          </span>
          <div className="flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/20">
            <input
              type="number"
              min="0"
              step="1"
              value={margin}
              onChange={(e) => setMargin(e.target.value)}
              data-testid="pricing-margin"
              placeholder="0"
              className="w-16 bg-transparent text-sm outline-none"
            />
            <span className="ml-1.5 text-xs text-slate-400">%</span>
          </div>
        </label>
        <div className="space-y-1">
          <span className="block text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
            Preço final (cliente)
          </span>
          <p
            data-testid="pricing-final-price"
            className="flex h-9 min-w-[120px] items-center rounded-lg bg-petrol/5 px-3 font-display text-sm font-bold text-petrol"
          >
            {finalPrice !== null ? brl.format(finalPrice) : "—"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={save}
            disabled={saving}
            data-testid="pricing-save-button"
            className="inline-flex h-9 items-center rounded-full bg-petrol px-4 text-xs font-semibold text-white transition-colors hover:bg-brand disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Salvar orçamento"}
          </button>
          <button
            onClick={downloadQuote}
            disabled={downloading}
            data-testid="pricing-download-quote"
            className="inline-flex h-9 items-center gap-2 rounded-full border border-petrol/20 px-4 text-xs font-semibold text-petrol transition-colors hover:border-brand hover:text-brand disabled:opacity-60"
          >
            {downloading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileDown className="h-3.5 w-3.5" />
            )}
            Baixar orçamento (PDF)
          </button>
          <button
            onClick={approve}
            disabled={approving}
            data-testid="pricing-approve-button"
            className="inline-flex h-9 items-center gap-2 rounded-full bg-emerald-600 px-4 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60"
          >
            {approving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <CheckCircle2 className="h-3.5 w-3.5" />
            )}
            Orçamento aprovado
          </button>
        </div>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <label className="space-y-1">
          <span className="block text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
            Diagnóstico do problema (prestador)
          </span>
          <textarea
            value={diagnosis}
            onChange={(e) => setDiagnosis(e.target.value)}
            rows={3}
            data-testid="pricing-diagnosis"
            placeholder="Ex.: Vazamento na conexão da torneira por desgaste do registro"
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
          />
        </label>
        <label className="space-y-1">
          <span className="block text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
            Solução do problema (prestador)
          </span>
          <textarea
            value={solution}
            onChange={(e) => setSolution(e.target.value)}
            rows={3}
            data-testid="pricing-solution"
            placeholder="Ex.: Troca do registro e vedação completa da conexão"
            className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
          />
        </label>
        <label className="space-y-1">
          <span className="block text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
            Prazo de conclusão da obra
          </span>
          <input
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            data-testid="pricing-deadline"
            placeholder="Ex.: 2 dias úteis após aprovação"
            className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
          />
        </label>
        <label className="space-y-1">
          <span className="block text-[0.65rem] font-semibold uppercase tracking-wider text-slate-400">
            Garantia do serviço
          </span>
          <input
            value={warranty}
            onChange={(e) => setWarranty(e.target.value)}
            data-testid="pricing-warranty"
            placeholder="Ex.: 90 dias contra o mesmo defeito"
            className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20"
          />
        </label>
      </div>
    </div>
  );
};
